import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import { Card } from '../../../components/ui/Card/Card';
import { Table, type Column } from '../../../components/ui/Table/Table';
import type { CustomerBankChange, CustomerBankHistoryResponse } from '../../../types/api';

const FIELD_LABEL: Record<CustomerBankChange['field_name'], string> = {
  account_name: 'Account name',
  account_number: 'Account number',
  sort_code: 'Sort code',
};

const SOURCE_LABEL: Record<string, string> = {
  edit_receipt: 'Staff edited receipt',
  receipt_create: 'Staff created receipt',
  receipt_upload: 'Driver app',
};

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/**
 * Batch 7b — admin-only, masked history of overwrites of this customer's
 * stored bank details. Renders nothing for non-admins and nothing at all when
 * there is no history, so it adds no noise for most customers.
 */
export function CustomerBankHistoryCard({
  uuid,
  isAdmin,
}: {
  uuid: string;
  isAdmin: boolean;
}): JSX.Element | null {
  const q = useQuery({
    queryKey: queryKeys.customers.bankHistory(uuid),
    queryFn: async () => {
      const res = await apiClient.get<CustomerBankHistoryResponse>(
        endpoints.customers.bankHistory(uuid),
      );
      return res.data;
    },
    enabled: isAdmin && Boolean(uuid),
    staleTime: 30_000,
  });

  if (!isAdmin) return null;
  const rows = q.data?.changes ?? [];
  if (q.isLoading || rows.length === 0) return null;

  const columns: Column<CustomerBankChange>[] = [
    { key: 'created_at', header: 'When', render: (v) => formatWhen(String(v)) },
    { key: 'field_name', header: 'Field', render: (v) => FIELD_LABEL[v as CustomerBankChange['field_name']] },
    { key: 'old_value', header: 'Was' },
    { key: 'new_value', header: 'Now' },
    {
      key: 'changed_by',
      header: 'Changed by',
      render: (_v, row) =>
        row.changed_by.name
          ? `${row.changed_by.name} (${row.changed_by.type === 'driver' ? 'driver' : 'staff'})`
          : '—',
    },
    { key: 'source_type', header: 'Source', render: (v) => SOURCE_LABEL[String(v)] ?? String(v) },
    {
      key: 'receipt_reference',
      header: 'Receipt',
      render: (_v, row) => (
        <Link to={`/collections/receipts/${encodeURIComponent(row.receipt_enc_id)}`}>
          {row.receipt_reference ?? 'View receipt'}
        </Link>
      ),
    },
  ];

  return (
    <Card title="Bank detail history">
      <Table<CustomerBankChange>
        columns={columns}
        data={rows}
        keyField={(r) => `${r.created_at}-${r.field_name}-${r.receipt_enc_id}`}
        ariaLabel="Bank detail history"
      />
    </Card>
  );
}
